import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContentContext } from '../../ContentContext';
import { ContentApi } from '../../../../shared/api/contentApi';
import PaginationFooter from '../../../../components/ui/PaginationFooter';
import NewItemDialog from '../components/NewItemDialog.jsx';
import { resolvePagedTotals } from '../../../../shared/helpers/pagination';
import formatDate from '../../../../shared/helpers/formatDate';
import UuidCell from '../../../../components/ui/UuidCell';
import TableCellPreview from '../../../../components/ui/TableCellPreview';
import { useListTableState } from '../../../../shared/hooks/useListTableState';

import { BaseTable } from '../../../../components/ui/ant/Table';
import { Flex, Input, Select } from 'antd';

const GLOBAL_GROUP_PAGE_SIZE = 200;

export default function ListEntityPage() {
    const navigate = useNavigate();
    const { projectId, versionId } = useContentContext();
    const [createOpen, setCreateOpen] = useState(false);
    const {
        qInput,
        setQInput,
        qDebounced,
        sortBy,
        setSortBy,
        sortDir,
        setSortDir,
        groupBy,
        setGroupBy,
        groupScope,
        setGroupScope,
        page,
        setPage,
        pageSize,
        setPageSize,
        total,
        setTotal,
        totalPages,
        setTotalPages,
        safePage,
    } = useListTableState({
        initialSortBy: 'name',
        initialSortDir: 'asc',
        initialGroupBy: 'none',
        initialPageSize: 6,
        debounceMs: 1450,
        enableGroupScope: true,
    });

    const [entities, setEntities] = useState([]);
    const [globalEntities, setGlobalEntities] = useState([]);
    const [valueTypeMap, setValueTypeMap] = useState({});

    const [loading, setLoading] = useState(false);
    const [globalLoading, setGlobalLoading] = useState(false);
    const [error, setError] = useState('');
    const [globalError, setGlobalError] = useState('');

    useEffect(() => {
        if (!projectId || !versionId) {
            setEntities([]);
            setTotal(0);
            setTotalPages(1);
            setLoading(false);
            return;
        }

        const ac = new AbortController();
        setLoading(true);
        setError('');

        ContentApi.getEntities(
            {
                search: qDebounced || undefined,
                page,
                pageSize,
            },
            { signal: ac.signal },
        )
            .then((data) => {
                setEntities(Array.isArray(data?.items) ? data.items : []);
                const paging = resolvePagedTotals(data, pageSize);
                setTotal(paging.total);
                setTotalPages(paging.totalPages);
            })
            .catch((e) => {
                if (!ac.signal.aborted) setError(e?.message || 'Ошибка загрузки');
            })
            .finally(() => !ac.signal.aborted && setLoading(false));

        return () => ac.abort();
    }, [page, pageSize, projectId, qDebounced, versionId]);

    useEffect(() => {
        if (groupBy !== 'valueTypeId') return;

        const ac = new AbortController();
        ContentApi.getValueTypes({ signal: ac.signal })
            .then((response) => {
                const items = Array.isArray(response)
                    ? response
                    : Array.isArray(response?.items)
                        ? response.items
                        : [];
                const map = items.reduce((acc, item) => {
                    const id = item?.id;
                    if (id) {
                        acc[id] = item?.code || item?.name || id;
                    }
                    return acc;
                }, {});
                setValueTypeMap(map);
            })
            .catch(() => {
                setValueTypeMap({});
            });

        return () => ac.abort();
    }, [groupBy]);

    useEffect(() => {
        if (!projectId || !versionId || groupBy === 'none' || groupScope !== 'global') {
            setGlobalEntities([]);
            setGlobalError('');
            setGlobalLoading(false);
            return;
        }

        const ac = new AbortController();

        const fetchAllPages = async () => {
            setGlobalLoading(true);
            setGlobalError('');

            try {
                const firstPage = await ContentApi.getEntities(
                    {
                        search: qDebounced || undefined,
                        page: 1,
                        pageSize: GLOBAL_GROUP_PAGE_SIZE,
                    },
                    { signal: ac.signal },
                );

                if (ac.signal.aborted) return;

                const firstItems = Array.isArray(firstPage?.items) ? firstPage.items : [];
                const paging = resolvePagedTotals(firstPage, GLOBAL_GROUP_PAGE_SIZE);
                const pageNumbers = Array.from(
                    { length: Math.max(0, paging.totalPages - 1) },
                    (_, index) => index + 2,
                );

                const aggregated = [...firstItems];
                const chunkSize = 5;

                for (let index = 0; index < pageNumbers.length; index += chunkSize) {
                    const chunk = pageNumbers.slice(index, index + chunkSize);
                    const responses = await Promise.all(
                        chunk.map((currentPage) =>
                            ContentApi.getEntities(
                                {
                                    search: qDebounced || undefined,
                                    page: currentPage,
                                    pageSize: GLOBAL_GROUP_PAGE_SIZE,
                                },
                                { signal: ac.signal },
                            ),
                        ),
                    );

                    if (ac.signal.aborted) return;

                    responses.forEach((response) => {
                        const items = Array.isArray(response?.items) ? response.items : [];
                        aggregated.push(...items);
                    });
                }

                setGlobalEntities(aggregated);
            } catch (e) {
                if (!ac.signal.aborted) {
                    setGlobalEntities([]);
                    setGlobalError(e?.message || 'Ошибка агрегированной загрузки');
                }
            } finally {
                if (!ac.signal.aborted) {
                    setGlobalLoading(false);
                }
            }
        };

        fetchAllPages();

        return () => ac.abort();
    }, [groupBy, groupScope, projectId, qDebounced, versionId]);

    const activeRows = useMemo(() => {
        if (groupBy !== 'none' && groupScope === 'global') {
            return globalEntities;
        }
        return entities;
    }, [entities, globalEntities, groupBy, groupScope]);

    const groupByKey = groupBy === 'none' ? undefined : groupBy;

    const groupByResolver = (row, groupField) => {
        if (groupField === 'entityTypes') {
            if (!Array.isArray(row?.entityTypes) || row.entityTypes.length === 0) {
                return 'Без типа';
            }
            return row.entityTypes.join(', ');
        }

        if (groupField === 'needsReview') {
            return row?.needsReview ? 'Требует проверки' : 'Проверено';
        }

        if (groupField === 'valueTypeId') {
            const valueTypeId = row?.valueTypeId;
            if (!valueTypeId) return 'Без valueType';
            return valueTypeMap[valueTypeId] || valueTypeId;
        }

        return row?.[groupField];
    };

    const groupLabelResolver = (key) => {
        if (groupBy === 'valueTypeId') {
            return `ValueType: ${key}`;
        }

        if (groupBy === 'entityTypes') {
            return `Тип: ${key}`;
        }

        return key;
    };

    const groupedWithGlobal = groupBy !== 'none' && groupScope === 'global';
    const displayLoading = loading || globalLoading;
    const displayError = error || globalError;

    return (
        <section className="p-4 md:p-6 space-y-4">
            <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="space-y-1">
                    <h1 className="text-xl font-semibold">Сущности</h1>
                    <p className="text-sm text-zinc-200">Данные с SbContentAPI</p>
                </div>
                <button
                    className="btn btn-soft-accent"
                    type="button"
                    onClick={() => navigate(`/content/entity/list`)}
                >
                    Карточки сущностей
                </button>
                <button
                    className="btn btn-soft-accent"
                    type="button"
                    onClick={() => setCreateOpen(true)}
                >
                    Создать сущность
                </button>
                <Flex flex wrap="wrap" gap="medium" align="end">
                    <Flex flex vertical>
                        <label className="eyebrow mb-1" htmlFor="entity-search">Поиск</label>
                        <Input
                            id="entity-search"
                            value={qInput}
                            onChange={(e) => setQInput(e.target.value)}
                            placeholder="Имя или systemName…"
                            className="w-64"
                        />
                    </Flex>

                    {groupBy !== 'none' && (
                        <Flex flex vertical>
                            <label className="eyebrow mb-1" htmlFor="entity-group-scope">Режим группировки</label>
                            <Select
                                id="entity-group-scope"
                                value={groupScope}
                                onChange={(value) => setGroupScope(value)}
                                options={[
                                    { value: 'page', label: 'Текущая страница' },
                                    { value: 'global', label: 'Все данные (агрегация)' },]}
                            />
                        </Flex>
                    )}
                </Flex>
            </header>

            <NewItemDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={(created) => {
                    const newId = created?.id || created?.itemId || created?.ItemId;
                    if (newId) {
                        navigate(`/content/entity/${newId}`);
                    } else {
                        setPage(1);
                    }
                }}
            />

            {displayError && (
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                    {displayError}
                </div>
            )}

            {groupedWithGlobal && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 text-blue-700 p-3 text-sm">
                    Глобальная группировка выполняется по всем данным версии. Это может занять некоторое время.
                </div>
            )}

            {displayLoading ? (
                <div className="p-6 text-sm text-zinc-500">Загрузка…</div>
            ) : (
                <>
                    <BaseTable
                        columns={[
                            {
                                key: 'id',
                                header: 'ID',
                                render: (value) => <UuidCell value={value} />,
                            },
                            { key: 'name', header: 'Имя', sortable: true },
                            {
                                key: 'description',
                                header: 'Описание',
                                render: (value) => <TableCellPreview value={value} maxLength={72} />,
                            },
                            { key: 'systemName', header: 'Системное имя', sortable: true },
                            {
                                key: 'valueTypeId',
                                header: 'Тип значения',
                                groupable: true,
                                render: (value) => <UuidCell value={value} />,
                            },
                            {
                                key: 'parameterGroupId',
                                header: 'Группа',
                                groupable: true,
                                render: (value) => <UuidCell value={value} />,
                            },
                            {
                                key: 'updatedAt',
                                header: 'Обновлено',
                                sortable: true,
                                render: (value) => <span title={value || '-'}>{formatDate(value)}</span>,
                            },
                        ]}
                        data={activeRows}
                        searchQuery={qInput}
                        searchKeys={['name', 'systemName', 'description']}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSortChange={(key, dir) => {
                            setSortBy(key);
                            if (dir) setSortDir(dir);
                        }}
                        groupBy={groupByKey}
                        onGroupByChange={(key) => setGroupBy(key ?? 'none')}
                        pagination={{
                            current: safePage,
                            pageSize,
                            total,
                        }}
                        onChange={(pagination) => {
                            if (pagination?.current) {
                                setPage(pagination.current);
                            }
                            if (pagination?.pageSize && pagination.pageSize !== pageSize) {
                                setPageSize(pagination.pageSize);
                            }
                        }}
                        groupByResolver={groupByResolver}
                        groupLabelResolver={groupLabelResolver}
                        onRowClick={(row) => navigate(`/content/entity/${row.id}`)}
                    />
                </>
            )}
        </section>
    );
}